import { yupResolver } from '@hookform/resolvers/yup'
import { TFunction } from 'i18next'
import { useEffect } from 'react'
import { UseFormReturn, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { ObjectSchema, object } from 'yup'

import { ContractsContract } from '@/Contract/application'
import { ContractsRepository } from '@/Contract/domain'
import {
  documentNumberSchema,
  nonEmptyPositiveNumberSchema,
  nonEmptyStringSchema,
  stringSchema,
  useMetaResponseHandler,
} from '@/Shared/ui/form'
import { useLoader } from '@/Shared/ui/loader'

export interface ContractForm {
  merchantName: string
  afiliateCode: number
  cif?: string

  street: string
  number: string
  floorBlockStair?: string
  postalCode: string
  city: string
  province: string

  additionalInformation?: string

  addressId: number
}

const schema = (t: TFunction<'translation', undefined>): ObjectSchema<ContractForm> =>
  object({
    merchantName: nonEmptyStringSchema(t, 50),
    afiliateCode: nonEmptyPositiveNumberSchema(t),
    cif: documentNumberSchema(t, true),

    street: nonEmptyStringSchema(t),
    number: nonEmptyStringSchema(t),
    floorBlockStair: stringSchema(t),
    postalCode: nonEmptyStringSchema(t),
    city: nonEmptyStringSchema(t),
    province: nonEmptyStringSchema(t),

    additionalInformation: stringSchema(t),

    addressId: nonEmptyPositiveNumberSchema(t),
  })

export interface IUseStoreController {
  form: UseFormReturn<ContractForm>
  save: (data: ContractForm) => Promise<void>
}

export const useContractDataController = (
  repository: ContractsRepository,
  id: number,
): IUseStoreController => {
  const { t } = useTranslation()
  const { startLoading, stopLoading } = useLoader()

  const { handleMetaResponse } = useMetaResponseHandler()

  const form = useForm<ContractForm>({
    resolver: yupResolver(schema(t)),
    mode: 'onChange',
  })

  const load = async (id: number): Promise<void> => {
    startLoading()

    const response = await ContractsContract(repository).getBillingData(id)

    stopLoading()

    if (handleMetaResponse(response?.meta, undefined, { notifySuccess: false })) {
      form.reset({
        ...form.getValues(),
        merchantName: response.data?.name ?? '',
        afiliateCode: response.data?.afiliateCode ?? 0,
        cif: response.data?.cif ?? '',

        street: response.data?.street ?? '',
        number: response.data?.number ?? '',
        floorBlockStair: response.data?.floorBlockStair ?? '',
        postalCode: response.data?.postalCode ?? '',
        city: response.data?.city ?? '',
        province: response.data?.province ?? '',

        additionalInformation: response.data?.additionalInfo ?? '',

        addressId: response.data?.addressId ?? 0,
      })
    }
  }

  const save = async (data: ContractForm): Promise<void> => {
    startLoading()

    const response = await ContractsContract(repository).saveBillingData(data)

    stopLoading()

    handleMetaResponse(response?.meta, form, { notifySuccess: true })
  }

  useEffect(() => {
    if (id) {
      load(id)
    }
  }, [id])

  return {
    form,
    save,
  }
}
